import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { getCoUserStats } from "../../../store/features/getCoUserStats";
import { getKbStats } from "../../../store/features/getKbStats";
import CssBaseline from "@mui/material/CssBaseline";
import OverviewCard from "../../../components/dashboardcomponents/Home/OverviewCard";
import TabNavigation from "../../../components/dashboardcomponents/Home/TabNavigation";
import TimeFilter from "../../../components/dashboardcomponents/Home/TimeFilter";
import BarChart from "../../../components/dashboardcomponents/Home/BarChart";
import GroupSelectDropdown from "../../../components/dashboardcomponents/ProfileComponents/GroupSelectDropdown";
import { tabs, timeOptions } from "../../../constants/index";
import { getChatStats } from "../../../store/features/getChatStats";
import { getTicketStats } from "../../../store/features/getTicketStats";
import { getChatGraphStats } from "../../../store/features/getChatGraphStats";
import { getTicketGraphStats } from "../../../store/features/getTicketGraphStats";
import { getChatSatisfactionGraphStats } from "../../../store/features/getChatSatisfactionGraphStats";
import { getWebsiteTrafficGraphStats } from "../../../store/features/getWebsiteTrafficGraphStats";
import { useAnalyticsGroupId } from "../../../hooks/useAnalyticsGroupId";
import { isHeaderInlinePageTitleEnabled } from "../../../config/featureFlags";
import "../TicketManagment/style.css";

const theme = createTheme({
  typography: {
    fontFamily: "Inter, sans-serif",
  },
});

const rangeFromFilter = (filter) =>
  filter === "7 days" ? "7d" : filter === "30 days" ? "30d" : "1y";

const DashboardMain = () => {
  const dispatch = useDispatch();
  const { selectedGroupId, setSelectedGroupId } = useAnalyticsGroupId();
  const { loading: chatLoading, stats: chatStats } = useSelector(
    (state) => state.chatStats
  );
  const { loading: ticketLoading, stats: ticketStats } = useSelector(
    (state) => state.ticketStats
  );
  const { loading: coUserLoading, stats: coUserStats } = useSelector(
    (state) => state.coUserStats || {}
  );
  const { loading: kbStatsLoading, stats: kbStats } = useSelector(
    (state) => state.kbStats || {}
  );
  const { loading: chatGraphLoading, data: chatGraphData } = useSelector(
    (state) => state.chatGraphStats
  );
  const { loading: ticketGraphLoading, data: ticketGraphData } = useSelector(
    (state) => state.ticketGraphStats || {}
  );
  const {
    loading: chatSatisfactionGraphLoading,
    data: chatSatisfactionGraphData,
  } = useSelector((state) => state.chatSatisfactionGraphStats || {});
  const { loading: websiteTrafficLoading, data: websiteTrafficData } =
    useSelector((state) => state.websiteTrafficGraphStats || {});

  const [activeTab, setActiveTab] = useState("Total Chats");
  const [activeTimeFilter, setActiveTimeFilter] = useState("7 days");

  const graphRange = useMemo(
    () => rangeFromFilter(activeTimeFilter),
    [activeTimeFilter]
  );

  // Overview cards — group-wise
  useEffect(() => {
    if (!selectedGroupId) return;
    dispatch(getChatStats({ group_id: selectedGroupId }));
    dispatch(getTicketStats({ group_id: selectedGroupId }));
    dispatch(getCoUserStats({ group_id: selectedGroupId }));
    dispatch(getKbStats({ group_id: selectedGroupId }));
  }, [dispatch, selectedGroupId]);

  // Stats graphs — group + timeline
  useEffect(() => {
    if (!selectedGroupId) return;

    if (activeTab === "Total Chats") {
      dispatch(
        getChatGraphStats({ range: graphRange, group_id: selectedGroupId })
      );
    }
    if (activeTab === "Website Traffic") {
      dispatch(
        getWebsiteTrafficGraphStats({
          range: graphRange,
          group_id: selectedGroupId,
        })
      );
    }
    if (activeTab === "Total Tickets") {
      dispatch(
        getTicketGraphStats({ range: graphRange, group_id: selectedGroupId })
      );
    }
    if (activeTab === "Chat Satisfaction") {
      dispatch(
        getChatSatisfactionGraphStats({
          range: graphRange,
          group_id: selectedGroupId,
        })
      );
    }
  }, [activeTimeFilter, activeTab, dispatch, selectedGroupId, graphRange]);

  const overviewData = [
    {
      title: "Chats",
      description: "Live conversations and messages",
      stats: [
        {
          label: "Total",
          value: chatStats?.total_chats?.toString() || "0",
        },
        {
          label: "Active",
          value: chatStats?.active_chats?.toString() || "0",
        },
        {
          label: "Unassigned",
          value: chatStats?.unassigned_chats?.toString() || "0",
        },
      ],
      loading: !selectedGroupId || chatLoading,
    },
    {
      title: "Tickets",
      description: "Support requests and follow ups",
      stats: [
        {
          label: "New",
          value: ticketStats?.new_tickets?.toString() || "0",
        },
        { label: "Open", value: ticketStats?.open_tickets?.toString() || "0" },
        {
          label: "Assigned to Me",
          value: ticketStats?.assigned_to_me?.toString() || "0",
        },
      ],
      loading: !selectedGroupId || ticketLoading,
    },
    {
      title: "Agents",
      description: "Active and inactive agents",
      stats: [
        {
          label: "Accepting Chats",
          value: (coUserStats?.accepting_chat ?? 0).toString(),
        },
        {
          label: "Not Accepting Chats",
          value: (coUserStats?.not_accepting_chat ?? 0).toString(),
        },
        { label: "Offline", value: (coUserStats?.offline ?? 0).toString() },
      ],
      loading: !selectedGroupId || !!coUserLoading,
    },
    {
      title: "Knowledge Base",
      description: "Articles and help guides",
      stats: [
        {
          label: "Articles",
          value: (kbStats?.articles ?? 0).toString(),
        },
        {
          label: "New Today",
          value: (kbStats?.new_today ?? 0).toString(),
        },
      ],
      loading: !selectedGroupId || !!kbStatsLoading,
    },
  ];

  const getChartData = () => {
    if (activeTab === "Total Chats" && chatGraphData) {
      return chatGraphData.map((item) => ({
        day: item.label,
        value: item.total_chats,
        avgCsat: item.avg_csat,
      }));
    }

    if (activeTab === "Website Traffic" && websiteTrafficData) {
      return websiteTrafficData.map((item) => ({
        day: item.label,
        value: item.totalVisitors,
        newVisitors: item.newVisitors,
      }));
    }

    if (activeTab === "Total Tickets" && ticketGraphData) {
      return ticketGraphData.map((item) => ({
        day: item.label,
        primary: item.new_tickets,
        secondary: item.open_tickets,
      }));
    }

    if (activeTab === "Chat Satisfaction" && chatSatisfactionGraphData) {
      return chatSatisfactionGraphData.map((item) => ({
        day: item.label,
        primary: item.good_count,
        secondary: item.bad_count,
        totalChats: item.total_chats,
        csat: item.csat,
      }));
    }

    return [];
  };

  const chartLoading =
    !selectedGroupId ||
    (activeTab === "Total Chats"
      ? chatGraphLoading
      : activeTab === "Website Traffic"
        ? !!websiteTrafficLoading
        : activeTab === "Total Tickets"
          ? ticketGraphLoading
          : activeTab === "Chat Satisfaction"
            ? chatSatisfactionGraphLoading
            : false);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ padding: "16px 24px 24px 24px", minHeight: "100vh" }}>
        {/* Legacy in-page title + group — hidden when header shows them (Application V2) */}
        {!isHeaderInlinePageTitleEnabled && (
          <div className="profile-header-row" style={{ marginBottom: "24px" }}>
            <Typography
              sx={{
                color: "#061522",
                fontFamily: "Inter",
                fontSize: "24px",
                fontWeight: 500,
                lineHeight: "18px",
              }}
            >
              Overview
            </Typography>
            <GroupSelectDropdown
              value={selectedGroupId}
              onSelect={setSelectedGroupId}
            />
          </div>
        )}

        <Grid container spacing={2} sx={{ marginBottom: "16px" }}>
          {overviewData.map((card, index) => (
            <Grid item key={index} xs={12} sm={6} md={4} lg={3} xl={3}>
              <OverviewCard
                title={card.title}
                description={card.description}
                stats={card.stats}
                loading={card.loading}
              />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            border: "1px solid #E5E9EC",
            borderRadius: "8px",
            backgroundColor: "#FFF",
            padding: "24px",
            marginTop: "16px",
          }}
        >
          <Typography
            sx={{
              color: "#061522",
              fontFamily: "Inter",
              fontSize: "20px",
              fontWeight: 500,
              lineHeight: "18px",
              marginBottom: "24px",
            }}
          >
            All Stats
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #E5E9EC",
              marginLeft: "-24px",
              marginRight: "-24px",
              paddingLeft: "24px",
              paddingRight: "24px",
              marginBottom: "24px",
            }}
          >
            <TabNavigation
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            <TimeFilter
              options={timeOptions}
              activeOption={activeTimeFilter}
              onOptionChange={setActiveTimeFilter}
            />
          </Box>
          <BarChart
            data={getChartData()}
            selectedTab={activeTab}
            activeTimeFilter={activeTimeFilter}
            loading={chartLoading}
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardMain;
